import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Mail, Phone, MapPin, Truck, ShieldCheck, Award, HelpCircle, FileText, Lock, Cookie } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSiteConfig } from '../context/SiteConfigContext';
import SafeHtml from '../components/SafeHtml';

// ============ Shared layout for static/legal pages ============
// If admin has set pages.{pageKey}.body_html, it overrides the children content.
const StaticLayout = ({ icon: Icon, title, breadcrumbLabel, children, pageKey }) => {
  const { config } = useSiteConfig();
  const cmsTitle = pageKey ? config[`pages.${pageKey}.title`] : null;
  const cmsBody = pageKey ? config[`pages.${pageKey}.body_html`] : null;
  const finalTitle = (cmsTitle && cmsTitle.trim()) || title;
  return (
    <div className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
          <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#2D2A26]">{breadcrumbLabel}</span>
        </nav>
        <div className="flex items-center gap-3 mb-6">
          {Icon && (
            <div className="w-12 h-12 rounded-xl bg-[#1F4D3F] text-white flex items-center justify-center">
              <Icon className="w-6 h-6" />
            </div>
          )}
          <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F]">{finalTitle}</h1>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E0D0] p-6 lg:p-10 max-w-none text-[#2D2A26]">
          {cmsBody && cmsBody.trim() ? (
            <SafeHtml html={cmsBody} />
          ) : children}
        </div>
      </div>
    </div>
  );
};

// ============ À propos ============
export const About = () => (
  <StaticLayout pageKey="about" icon={Award} title="À propos de Comptoir-Online" breadcrumbLabel="À propos">
    <p className="text-lg leading-relaxed">
      <strong>Comptoir-Online</strong> est la première plateforme algérienne de comparaison de prix
      dédiée aux produits dentaires. Nous mettons en relation les cabinets dentaires, cliniques et laboratoires
      avec des dizaines de fournisseurs locaux vérifiés, pour vous garantir le meilleur prix sur chaque achat.
    </p>
    <h2 className="font-serif text-2xl text-[#1F4D3F] mt-8 mb-3">Notre mission</h2>
    <p>
      Démocratiser l'accès aux produits dentaires en Algérie en apportant transparence des prix,
      fraîcheur des stocks et confiance entre acheteurs et fournisseurs. Économisez jusqu'à 60% en comparant
      systématiquement.
    </p>
    <h2 className="font-serif text-2xl text-[#1F4D3F] mt-8 mb-3">Pour qui ?</h2>
    <ul className="list-disc pl-6 space-y-1.5">
      <li><strong>Cabinets dentaires</strong> : trouvez vos consommables au meilleur prix, livraison 48h.</li>
      <li><strong>Cliniques & hôpitaux</strong> : commandez en gros, sourcez plusieurs fournisseurs.</li>
      <li><strong>Laboratoires de prothèse</strong> : alliages, résines, équipements à comparer.</li>
      <li><strong>Fournisseurs</strong> : touchez tous les professionnels d'Algérie via une vitrine claire.</li>
    </ul>
    <h2 className="font-serif text-2xl text-[#1F4D3F] mt-8 mb-3">Nos engagements</h2>
    <ul className="list-disc pl-6 space-y-1.5">
      <li>Fournisseurs vérifiés par notre équipe</li>
      <li>Prix et stocks actualisés régulièrement</li>
      <li>Avis vérifiés par achat</li>
      <li>Paiement à la livraison (COD) — pas de surprise</li>
    </ul>
  </StaticLayout>
);

// ============ Comment ça marche ============
export const HowItWorksPage = () => (
  <StaticLayout pageKey="how" icon={HelpCircle} title="Comment ça marche ?" breadcrumbLabel="Comment ça marche">
    <p>Acheter sur Comptoir-Online se fait en 3 étapes simples :</p>
    <ol className="list-decimal pl-6 space-y-3 mt-4">
      <li><strong>Recherchez</strong> votre produit dentaire (composite, gants, fraises, anesthésiques, etc.).</li>
      <li><strong>Comparez</strong> les offres de tous nos fournisseurs côte à côte : prix, stock, fraîcheur de l'offre, localisation.</li>
      <li><strong>Commandez</strong> au meilleur prix. Paiement à la livraison dans toute l'Algérie sous 48h.</li>
    </ol>
    <h2 className="font-serif text-2xl text-[#1F4D3F] mt-8 mb-3">Vous êtes fournisseur ?</h2>
    <p>
      <Link to="/inscription?role=supplier" className="text-[#1F4D3F] underline">Inscrivez-vous gratuitement</Link> et publiez votre catalogue.
      Notre équipe valide votre compte sous 48h, puis vous gérez vos prix, stocks et disponibilités depuis votre tableau de bord.
    </p>
  </StaticLayout>
);

// ============ Tarifs ============
export const Pricing = () => (
  <StaticLayout icon={Award} title="Tarifs" breadcrumbLabel="Tarifs">
    <p className="text-lg"><strong>Comptoir-Online est entièrement gratuit</strong>, autant pour les acheteurs que pour les fournisseurs. Aucun abonnement, aucune commission, aucun engagement.</p>
    <div className="mt-6 bg-[#F5F1EA] border border-[#E8E0D0] rounded-xl p-5">
      <div className="text-sm uppercase tracking-wider text-[#1F4D3F] font-medium">Lancement</div>
      <div className="font-serif text-3xl text-[#1F4D3F] font-semibold mt-1">100 % gratuit</div>
      <ul className="mt-3 space-y-1.5 text-sm text-[#2D2A26]">
        <li>✓ Catalogue illimité</li>
        <li>✓ Tableau de bord complet en temps réel</li>
        <li>✓ Import CSV en masse</li>
        <li>✓ Mise à jour prix en masse</li>
        <li>✓ Aucune commission sur les ventes</li>
        <li>✓ Support disponible 7j/7</li>
      </ul>
      <p className="mt-4 text-sm text-[#7A6F5C]">
        Notre objectif est de bâtir la plus grande communauté de fournisseurs dentaires en Algérie. Nous prendrons notre temps avant d'introduire d'éventuels services premium — et vous serez consultés avant.
      </p>
    </div>
    <p className="mt-6 text-sm text-[#7A6F5C]">Une question ? Écrivez-nous à <a href="mailto:fournisseurs@dentadz.com" className="text-[#1F4D3F] underline">fournisseurs@dentadz.com</a>.</p>
  </StaticLayout>
);

// ============ Contact ============
export const Contact = () => (
  <StaticLayout pageKey="contact" icon={Mail} title="Contactez-nous" breadcrumbLabel="Contact">
    <p>Une question, une réclamation ou un partenariat ? Notre équipe vous répond sous 24h ouvrées.</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
      <div className="border border-[#E8E0D0] rounded-xl p-5">
        <h3 className="font-medium text-[#2D2A26] flex items-center gap-2"><Mail className="w-4 h-4 text-[#1F4D3F]" /> Email</h3>
        <p className="mt-2 text-sm"><a href="mailto:contact@dentadz.com" className="text-[#1F4D3F] underline">contact@dentadz.com</a></p>
        <p className="text-sm"><a href="mailto:fournisseurs@dentadz.com" className="text-[#1F4D3F] underline">fournisseurs@dentadz.com</a></p>
      </div>
      <div className="border border-[#E8E0D0] rounded-xl p-5">
        <h3 className="font-medium text-[#2D2A26] flex items-center gap-2"><Phone className="w-4 h-4 text-[#1F4D3F]" /> Téléphone</h3>
        <p className="mt-2 text-sm">+213 21 00 00 22 (Conseils & formations)</p>
        <p className="text-sm">+213 21 00 00 11 (Commandes)</p>
      </div>
      <div className="border border-[#E8E0D0] rounded-xl p-5 md:col-span-2">
        <h3 className="font-medium text-[#2D2A26] flex items-center gap-2"><MapPin className="w-4 h-4 text-[#1F4D3F]" /> Adresse</h3>
        <p className="mt-2 text-sm">Comptoir-Online SARL — Cité des Affaires, Alger Centre, Algérie</p>
      </div>
    </div>
  </StaticLayout>
);

// ============ FAQ ============
const FAQ_ITEMS = [
  { q: "Comment passer une commande ?", a: "Recherchez un produit, comparez les offres des fournisseurs, ajoutez au panier, puis validez. Vous recevrez une confirmation par email." },
  { q: "Quels sont les modes de paiement ?", a: "Actuellement, paiement à la livraison (COD) uniquement. Le paiement en ligne (CIB / EDahabia) sera bientôt disponible." },
  { q: "Quels sont les délais de livraison ?", a: "Livraison sous 48h dans la plupart des wilayas. Livraison gratuite à partir de 25 000 DA d'achats." },
  { q: "Puis-je retourner un produit ?", a: "Oui, sous 7 jours, sauf produits ouverts ou périssables. Contactez directement le fournisseur depuis votre espace 'Mes commandes'." },
  { q: "Comment devenir fournisseur ?", a: "Inscrivez-vous via 'Devenir fournisseur'. Notre équipe valide votre compte sous 48h." },
  { q: "Les avis sont-ils vérifiés ?", a: "Oui, seuls les acheteurs ayant réellement commandé un produit peuvent laisser un avis." },
  { q: "Pourquoi des prix si différents pour le même produit ?", a: "Chaque fournisseur fixe ses propres prix selon son volume, sa marge et sa localisation. C'est précisément pour ça que Comptoir-Online existe : pour vous faire économiser." },
];
export const FAQ = () => (
  <StaticLayout pageKey="faq" icon={HelpCircle} title="Foire aux questions" breadcrumbLabel="FAQ">
    <div className="space-y-3 not-prose">
      {FAQ_ITEMS.map((item, idx) => (
        <details key={idx} className="border border-[#E8E0D0] rounded-xl p-4 group">
          <summary className="cursor-pointer font-medium text-[#2D2A26] flex items-center justify-between">
            {item.q}
            <ChevronRight className="w-4 h-4 text-[#7A6F5C] group-open:rotate-90 transition" />
          </summary>
          <p className="mt-3 text-sm text-[#5A5247]">{item.a}</p>
        </details>
      ))}
    </div>
  </StaticLayout>
);

// ============ Livraison ============
export const Shipping = () => (
  <StaticLayout pageKey="shipping" icon={Truck} title="Livraison" breadcrumbLabel="Livraison">
    <h2 className="font-serif text-2xl text-[#1F4D3F] mb-3">Zones de livraison</h2>
    <p>Nous livrons dans les 58 wilayas d'Algérie via nos partenaires logistiques de confiance.</p>
    <h2 className="font-serif text-2xl text-[#1F4D3F] mt-8 mb-3">Délais</h2>
    <ul className="list-disc pl-6 space-y-1.5">
      <li><strong>Grand Alger, Oran, Constantine</strong> : 24–48h</li>
      <li><strong>Autres wilayas du Nord</strong> : 48–72h</li>
      <li><strong>Sud algérien</strong> : 3–5 jours ouvrés</li>
    </ul>
    <h2 className="font-serif text-2xl text-[#1F4D3F] mt-8 mb-3">Frais de port</h2>
    <ul className="list-disc pl-6 space-y-1.5">
      <li>Forfait livraison : <strong>500 DA</strong></li>
      <li><strong>Gratuit</strong> à partir de 25 000 DA d'achats</li>
    </ul>
    <h2 className="font-serif text-2xl text-[#1F4D3F] mt-8 mb-3">Suivi</h2>
    <p>Le suivi de votre commande est disponible depuis votre espace <Link to="/mes-commandes" className="text-[#1F4D3F] underline">Mes commandes</Link>.</p>
  </StaticLayout>
);

// ============ CGV ============
export const Terms = () => (
  <StaticLayout pageKey="terms" icon={FileText} title="Conditions générales de vente" breadcrumbLabel="CGV">
    <p className="text-sm text-[#7A6F5C]">Dernière mise à jour : Janvier 2026</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">1. Objet</h2>
    <p>Les présentes CGV régissent l'utilisation de la plateforme Comptoir-Online et les transactions entre acheteurs (cabinets dentaires) et fournisseurs.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">2. Rôle de Comptoir-Online</h2>
    <p>Comptoir-Online agit en tant qu'intermédiaire technique entre acheteurs et fournisseurs. Les transactions et factures sont conclues directement entre l'acheteur et le fournisseur.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">3. Commande</h2>
    <p>Toute commande passée sur la plateforme implique acceptation pleine et entière des présentes CGV. Le fournisseur dispose de 24h pour confirmer la commande, sans quoi celle-ci pourra être annulée automatiquement.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">4. Prix et paiement</h2>
    <p>Les prix sont en Dinars Algériens (DA), toutes taxes comprises. Paiement à la livraison (COD) uniquement.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">5. Livraison</h2>
    <p>Les délais et frais de livraison sont précisés sur la page <Link to="/livraison" className="text-[#1F4D3F] underline">Livraison</Link>.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">6. Droit de rétractation</h2>
    <p>Conformément à la législation algérienne, l'acheteur professionnel dispose d'un délai de 7 jours pour signaler tout défaut ou non-conformité, sauf produits descellés (médicaments, consommables stériles).</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">7. Responsabilité</h2>
    <p>Comptoir-Online n'est pas responsable de la qualité intrinsèque des produits, qui relève des fournisseurs et des fabricants. Comptoir-Online vérifie en revanche la légitimité des fournisseurs avant leur référencement.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">8. Litiges</h2>
    <p>Tout litige sera réglé en priorité à l'amiable via notre support. À défaut, les tribunaux d'Alger seront seuls compétents.</p>
  </StaticLayout>
);

// ============ Mentions légales ============
export const Legal = () => (
  <StaticLayout pageKey="legal" icon={FileText} title="Mentions légales" breadcrumbLabel="Mentions légales">
    <h2 className="font-serif text-xl text-[#1F4D3F] mb-2">Éditeur du site</h2>
    <p>Comptoir-Online SARL — Société à responsabilité limitée<br />
      Capital social : 1 000 000 DA<br />
      RC : XX/XXX/XXXXXXX — NIF : XXXXXXXXXXXX<br />
      Siège social : Cité des Affaires, Alger Centre, Algérie<br />
      Email : contact@dentadz.com — Téléphone : +213 21 00 00 22</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Directeur de la publication</h2>
    <p>Le Gérant de Comptoir-Online SARL.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Hébergement</h2>
    <p>Le site est hébergé par Emergent Cloud Platform.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Propriété intellectuelle</h2>
    <p>L'ensemble du contenu du site (textes, marques, logos, images, design) est protégé par les lois algériennes et internationales relatives à la propriété intellectuelle. Toute reproduction non autorisée est interdite.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Crédit photos</h2>
    <p>Les images produits sont fournies par les fournisseurs référencés ou proviennent de banques d'images libres de droits.</p>
  </StaticLayout>
);

// ============ Confidentialité ============
export const Privacy = () => (
  <StaticLayout pageKey="privacy" icon={Lock} title="Politique de confidentialité" breadcrumbLabel="Confidentialité">
    <p className="text-sm text-[#7A6F5C]">Dernière mise à jour : Janvier 2026</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Données collectées</h2>
    <p>Nous collectons les données strictement nécessaires : nom, email, mot de passe (haché), adresse de livraison, historique de commandes, avis publiés.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Utilisation</h2>
    <p>Vos données servent uniquement à : permettre votre identification, traiter vos commandes, vous envoyer des emails transactionnels, améliorer notre service. Aucune revente à des tiers.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Vos droits</h2>
    <p>Conformément à la loi algérienne 18-07 sur la protection des données personnelles, vous disposez d'un droit d'accès, de rectification, de portabilité et de suppression de vos données. Pour exercer ces droits, écrivez à <a href="mailto:privacy@dentadz.com" className="text-[#1F4D3F] underline">privacy@dentadz.com</a>.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Conservation</h2>
    <p>Vos données sont conservées pendant la durée nécessaire à la relation commerciale, puis archivées en conformité avec la législation (3 ans pour les transactions).</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Sécurité</h2>
    <p>Vos données sont stockées sur serveurs sécurisés. Vos mots de passe sont hachés (bcrypt). Les connexions sont chiffrées (HTTPS).</p>
  </StaticLayout>
);

// ============ Cookies ============
export const Cookies = () => (
  <StaticLayout pageKey="cookies" icon={Cookie} title="Gestion des cookies" breadcrumbLabel="Cookies">
    <p>Comptoir-Online utilise des cookies pour le bon fonctionnement du site et l'amélioration de votre expérience.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Cookies essentiels</h2>
    <p>Indispensables au fonctionnement du site (authentification, panier, préférences). Ils ne peuvent pas être désactivés.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Cookies analytiques</h2>
    <p>Nous utilisons un cookie pour mesurer le trafic et améliorer le site. Aucune donnée personnelle n'est partagée avec des tiers.</p>
    <h2 className="font-serif text-xl text-[#1F4D3F] mt-6 mb-2">Cookies tiers</h2>
    <p>Aucun cookie tiers marketing n'est utilisé.</p>
  </StaticLayout>
);

// ============ Blog placeholder ============
export const Blog = () => (
  <StaticLayout icon={FileText} title="Blog Comptoir-Online" breadcrumbLabel="Blog">
    <p className="text-center text-[#7A6F5C] py-10">
      📝 Notre blog est en cours de préparation.
      Bientôt : guides d'achat, comparatifs de produits, actualités du secteur dentaire en Algérie.
    </p>
    <div className="text-center">
      <Link to="/"><Button className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">Retour à l'accueil</Button></Link>
    </div>
  </StaticLayout>
);
